import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { ImageUploadService, EntityType } from '../../services/imageUploadService';

interface ImageUploadComponentProps {
  entityType: EntityType;
  entityId: string;
  entityName?: string;
  currentImageUrl?: string;
  onUploadSuccess?: (imageUrl: string) => void;
  onDeleteSuccess?: () => void;
}

export const ImageUploadComponent: React.FC<ImageUploadComponentProps> = ({
  entityType,
  entityId,
  entityName,
  currentImageUrl: initialImageUrl,
  onUploadSuccess,
  onDeleteSuccess
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 초기 이미지 URL 로드
    if (!initialImageUrl && entityId) {
      ImageUploadService.getImageUrl(entityType, entityId).then(url => {
        if (url) setImageUrl(url);
      });
    }
  }, [entityType, entityId, initialImageUrl]);

  const handleFileSelect = async (file: File) => {
    if (!file || !entityId) return;

    setIsUploading(true);
    setError(null);

    const result = await ImageUploadService.uploadImage(file, entityType, entityId);

    if (result.success && result.publicUrl) {
      setImageUrl(result.publicUrl);
      onUploadSuccess?.(result.publicUrl);
    } else {
      setError(result.error || '업로드 실패');
    }

    setIsUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('정말로 이미지를 삭제하시겠습니까?')) return;

    setIsUploading(true);
    setError(null);

    const result = await ImageUploadService.deleteImage(entityType, entityId);

    if (result.success) {
      setImageUrl(null);
      onDeleteSuccess?.();
    } else {
      setError(result.error || '삭제 실패');
    }

    setIsUploading(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const getEntityTypeLabel = () => {
    switch (entityType) {
      case 'skill': return '스킬';
      case 'effect': return '효과';
      case 'entity': return '엔티티';
    }
  };

  return (
    <div className="image-upload-component">
      <div className="upload-header">
        <h4 className="text-sm font-medium text-gray-700">
          {getEntityTypeLabel()} 아이콘
          {entityName && <span className="text-gray-500 ml-2">({entityName})</span>}
        </h4>
      </div>

      {error && (
        <div className="alert alert-error mt-2">
          <span className="text-sm">{error}</span>
        </div>
      )}

      {imageUrl ? (
        <div className="image-preview mt-3">
          <div className="relative inline-block">
            <img
              src={imageUrl}
              alt={`${entityName || entityId} icon`}
              className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
            />
            {!isUploading && (
              <button
                onClick={handleDelete}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                title="이미지 삭제"
              >
                <X size={16} />
              </button>
            )}
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <Loader2 className="animate-spin text-white" size={24} />
              </div>
            )}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            다른 이미지로 변경
          </button>
        </div>
      ) : (
        <div
          className={`upload-area mt-3 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          } ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="animate-spin mx-auto text-gray-400" size={40} />
          ) : (
            <>
              <ImageIcon className="mx-auto text-gray-400" size={40} />
              <p className="mt-2 text-sm text-gray-600">
                클릭하거나 이미지를 드래그하여 업로드
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, WebP, GIF (최대 5MB)
              </p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default ImageUploadComponent;